import React, { useEffect, useState, useMemo, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import { collection, getDocs } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { db } from '../../firebase';
import './AdminReportsDrivers.css';
import '../../styles/filters.css';
import { exportToPDFArabic } from '../../utils/pdfExporter';

const AdminReportsDrivers = () => {
  const [topDrivers, setTopDrivers] = useState([]);
  const [topDriverFees, setTopDriverFees] = useState([]);
  const [driverOrdersByStatus, setDriverOrdersByStatus] = useState([]);
  const [driverRevenue, setDriverRevenue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedOrdersCount, setCompletedOrdersCount] = useState(0);
  const [allOrders, setAllOrders] = useState([]);
  
  // إضافة حالات للفلاتر
  const [dateRange, setDateRange] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  const STATUS_COLORS = useMemo(() => ({
    'تم التسليم ✅': '#28a745',
    'جاري التوصيل 🚚': '#17a2b8',
    'ملغي ❌': '#dc3545',
    'جديد': '#007bff',
    'غير معروف': '#6c757d'
  }), []);

  // دالة لتطبيق الفلاتر
  const applyFilters = useCallback((orders) => {
    let filtered = [...orders];
    
    // فلترة حسب التاريخ
    if (dateRange !== 'all') {
      const now = new Date();
      filtered = filtered.filter(order => {
        if (!order.createdAt) return false;
        const orderDate = order.createdAt.toDate();
        
        switch (dateRange) {
          case 'today':
            return orderDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date(now.setDate(now.getDate() - 7));
            return orderDate >= weekAgo;
          case 'month':
            return orderDate.getMonth() === now.getMonth() && 
                   orderDate.getFullYear() === now.getFullYear();
          case 'year':
            return orderDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }
    
    // فلترة حسب السائق
    if (selectedDriver !== 'all') {
      filtered = filtered.filter(order => order.driverId === selectedDriver);
    }
    
    // فلترة حسب الحالة
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(order => order.status === selectedStatus);
    }
    
    return filtered;
  }, [dateRange, selectedDriver, selectedStatus]);

  useEffect(() => {
    const fetchDriverReports = async () => {
      try {
        setLoading(true);
        // جلب جميع الطلبات
        const snapshot = await getDocs(collection(db, 'orders'));
        const allOrders = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setAllOrders(allOrders);
        
        // تطبيق الفلاتر
        const filteredOrders = applyFilters(allOrders);
        
        // جلب الطلبات التي تم توصيلها بشكل دقيق
        const completedOrders = filteredOrders.filter(order => order.status === 'تم التسليم ✅');
        
        console.log('إجمالي عدد الطلبات:', allOrders.length);
        console.log('عدد الطلبات المفلترة:', filteredOrders.length);
        console.log('عدد الطلبات المكتملة:', completedOrders.length);
        
        setCompletedOrdersCount(completedOrders.length);
  
        // إحصائيات عدد الطلبات والرسوم لكل سائق
        const countMap = {};
        const feeMap = {};
        const revenueMap = {};
        
        // تجميع البيانات حسب حالة الطلب للسائقين 
        const driverStatusMap = {};
        
        // معالجة الطلبات المفلترة
        filteredOrders.forEach(order => {
          const status = order.status || 'غير معروف';
          
          // حساب الطلبات لكل حالة
          driverStatusMap[status] = driverStatusMap[status] || {
            name: status,
            value: 0,
            color: STATUS_COLORS[status] || '#6c757d'
          };
          driverStatusMap[status].value += 1;
          
          // جمع بيانات السائق لجميع الطلبات بغض النظر عن الحالة
          if (order.driverId) {
            const name = order.driverName || 'غير معروف';
            const id = order.driverId || 'unknown';
            const key = `${id}_${name}`;
            
            // إجمالي الإيرادات لكل سائق (لجميع الطلبات)
            revenueMap[key] = revenueMap[key] || { 
              name, 
              totalOrders: 0, 
              totalRevenue: 0, 
              completedOrders: 0,
              pendingOrders: 0,
              canceledOrders: 0,
              newOrders: 0
            };
            
            revenueMap[key].totalOrders += 1;
            revenueMap[key].totalRevenue += (order.total || 0);
            
            // تصنيف الطلبات حسب الحالة
            if (status === 'تم التسليم ✅') {
              revenueMap[key].completedOrders += 1;
            } else if (status === 'جاري التوصيل 🚚') {
              revenueMap[key].pendingOrders += 1;
            } else if (status === 'ملغي ❌') {
              revenueMap[key].canceledOrders += 1;
            } else if (status === 'جديد') {
              revenueMap[key].newOrders += 1;
            }
          }
        });
        
        // معالجة الطلبات المكتملة فقط
        completedOrders.forEach(order => {
          const name = order.driverName || 'غير معروف';
          const id = order.driverId || 'unknown';
          const key = `${id}_${name}`;
          
          // عدد الطلبات المكتملة
          countMap[key] = countMap[key] || { name, count: 0 };
          countMap[key].count += 1;
          
          // رسوم التوصيل المكتملة
          feeMap[key] = feeMap[key] || { name, totalFee: 0, earnings: 0, orderCount: 0 };
          feeMap[key].totalFee += (order.deliveryFee || 0);
          feeMap[key].earnings += (order.deliveryFee || 0) * 0.8; // افتراض أن السائق يحصل على 80% من رسوم التوصيل 
          feeMap[key].orderCount += 1;
        });
  
        // ترتيب السائقين حسب عدد الطلبات
        const topDriversList = Object.values(countMap)
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
  
        // ترتيب السائقين حسب رسوم التوصيل
        const topFeesList = Object.values(feeMap)
          .map(item => ({
            ...item,
            totalFee: parseFloat(item.totalFee.toFixed(3)),
            avgFee: parseFloat((item.totalFee / (item.orderCount || 1)).toFixed(3)),
            earnings: parseFloat(item.earnings.toFixed(3))
          }))
          .sort((a, b) => b.totalFee - a.totalFee)
          .slice(0, 10);
          
        // ترتيب السائقين حسب الإيرادات الكلية
        const driverRevenueList = Object.values(revenueMap)
          .map(item => ({
            ...item,
            totalRevenue: parseFloat(item.totalRevenue.toFixed(3)),
            avgRevenue: parseFloat((item.totalRevenue / (item.totalOrders || 1)).toFixed(3))
          }))
          .sort((a, b) => b.totalRevenue - a.totalRevenue)
          .slice(0, 10);
  
        setTopDrivers(topDriversList);
        setTopDriverFees(topFeesList);
        setDriverOrdersByStatus(Object.values(driverStatusMap));
        setDriverRevenue(driverRevenueList);
        
      } catch (error) {
        console.error('خطأ في جلب البيانات:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
  
    fetchDriverReports();
  }, [applyFilters, STATUS_COLORS]);
  
  // دالة تصدير PDF
  const handleExportToPDF = () => {
    const filters = {
      dateRange: dateRange,
      driver: selectedDriver,
      status: selectedStatus
    };
    
    exportToPDFArabic(driverRevenue, filters);
  };

  // الحصول على قائمة السائقين المتوفرين
  const availableDrivers = useMemo(() => {
    const drivers = [...new Set(allOrders.map(order => ({
      id: order.driverId,
      name: order.driverName
    })))];
    return drivers.filter(d => d.id && d.name);
  }, [allOrders]);
  
  if (loading) {
    return (
      <div className="restaurant-dashboard">
        <AdminSidebar />
        <div className="restaurant-dashboard-content">
          <AdminHeader adminName="تقارير السائقين" />
          <div className="loading-container">
            <div className="spinner"></div>
            <p>جاري تحميل البيانات...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="restaurant-dashboard">
        <AdminSidebar />
        <div className="restaurant-dashboard-content">
          <AdminHeader adminName="تقارير السائقين" />
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={() => window.location.reload()}>
              إعادة المحاولة
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (    
    <div className="restaurant-dashboard">
      <AdminSidebar />
      <div className="restaurant-dashboard-content">
        <AdminHeader adminName="تقارير السائقين" />

        <div className="restaurant-dashboard-welcome">
          <h1>تقارير السائقين</h1>
          <p>الرسوم البيانية والإحصائيات الخاصة بأداء السائقين</p>
        </div>

        {/* الفلاتر */}
        <div className="filters-section">
          <div className="filters-group">
            <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
              <option value="all">جميع الفترات</option>
              <option value="today">اليوم</option>
              <option value="week">هذا الأسبوع</option>
              <option value="month">هذا الشهر</option>
              <option value="year">هذه السنة</option>
            </select>
            
            <select value={selectedDriver} onChange={(e) => setSelectedDriver(e.target.value)}>
              <option value="all">جميع السائقين</option>
              {availableDrivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.name}
                </option>
              ))}
            </select>
            
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              <option value="all">جميع الحالات</option>
              <option value="تم التسليم ✅">تم التسليم</option>
              <option value="جاري التوصيل 🚚">جاري التوصيل</option>
              <option value="ملغي ❌">ملغي</option>
              <option value="جديد">جديد</option>
            </select>
            
            <button onClick={handleExportToPDF} className="export-btn pdf">
              تصدير PDF
              <i className="fas fa-file-pdf"></i>
            </button>
          </div>
        </div>

        <div className="statistics-summary">
          <div className="stat-card">
            <h4>عدد السائقين النشطين</h4>
            <p>{topDrivers.length || driverRevenue.length}</p>
          </div>
          <div className="stat-card">
            <h4>إجمالي التوصيلات المكتملة</h4>
            <p>{completedOrdersCount}</p>
          </div>
          <div className="stat-card">
            <h4>إجمالي رسوم التوصيل</h4>
            <p>{topDriverFees.reduce((sum, item) => sum + item.totalFee, 0).toFixed(3)} د.ك</p>
          </div>
        </div>

        {/* حالات الطلبات */}
        <div className="report-chart-section">
          <h3>توزيع الطلبات حسب الحالة</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={driverOrdersByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                >
                  {driverOrdersByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip formatter={(value) => [`${value} طلب`, 'العدد']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* باقي المحتوى كما هو */}
        <div className="report-chart-section">
          <h3>إجمالي إيرادات الطلبات لكل سائق (د.ك)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={driverRevenue} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} د.ك`} />
              <Bar dataKey="totalRevenue" fill="#8884d8" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="report-chart-section">
          <h3>أكثر السائقين نشاطًا (حسب عدد التوصيلات المكتملة)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topDrivers} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} طلب`} />
              <Bar dataKey="count" fill="#ff9800" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="report-chart-section">
          <h3>أرباح السائقين من رسوم التوصيل (د.ك)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topDriverFees} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} د.ك`} />
              <Bar dataKey="earnings" fill="#4caf50" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="report-chart-section">
          <h3>متوسط رسوم التوصيل لكل سائق (د.ك)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topDriverFees} layout="vertical" margin={{ left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={120} />
              <Tooltip formatter={(value) => `${value} د.ك`} />
              <Bar dataKey="avgFee" fill="#17a2b8" barSize={24} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="detailed-table">
          <h3>تفاصيل البيانات المالية للسائقين</h3>
          <table>
            <thead>
              <tr>
                <th>السائق</th>
                <th>عدد الطلبات</th>
                <th>مكتملة</th>
                <th>قيد التوصيل</th>
                <th>ملغية</th>
                <th>رسوم التوصيل (د.ك)</th>
                <th>إجمالي الإيرادات (د.ك)</th>
                <th>أرباح السائق (د.ك)</th>
              </tr>
            </thead>
            <tbody>
              {driverRevenue.map((item, index) => {
                const feeInfo = topDriverFees.find(fee => fee.name === item.name) || { totalFee: 0, earnings: 0 };
                return (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.totalOrders}</td>
                    <td>{item.completedOrders}</td>
                    <td>{item.pendingOrders}</td>
                    <td>{item.canceledOrders}</td>
                    <td>{feeInfo.totalFee}</td>
                    <td>{item.totalRevenue}</td>
                    <td>{feeInfo.earnings}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminReportsDrivers;